import React, { useState, useEffect, useCallback, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { XMarkIcon, StarIcon, MapPinIcon, PencilIcon, TrashIcon, ArrowTopRightOnSquareIcon, ArrowsPointingOutIcon, ArrowsPointingInIcon } from '@heroicons/react/24/solid'
import { StarIcon as StarOutline } from '@heroicons/react/24/outline'
import googleIcon from '../../media/icons/google.svg'
import loadfetcherLogo from '../../media/icons/logo-brand.svg'
import { getFacilityStats, getAllFacilityReviews, submitFacilityReview, deleteFacilityReview, getFacilityNearby, getFacilityLoadActivity } from '../../services/httpService'
import { getCurrentUser } from '../../services/authService'
import { useDarkMode } from '../../context/DarkModeContext'
import { escapeHtml } from '../Maps/mapUtils'
import FacilityLoader from './FacilityLoader'
import LoadActivityTab from './LoadActivityTab'

const DISTRIBUTION_LABELS = {
	'0-1': '0-1 hour early',
	'1-2': '1-2 hours early',
	'2-4': '2-4 hours early',
	'4-8': '4-8 hours early',
	'8-24': '8-24 hours early',
	'24+': '24+ hours early',
}

function DistributionBar({ label, percent, isDarkMode }) {
	return (
		<div className="flex items-center gap-2 text-xs">
			<span className="w-24 text-gray-500 truncate">{label}</span>
			<div className={`flex-1 h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
				<div className="h-full bg-blue-500 rounded-full" style={{ width: `${percent}%` }} />
			</div>
			<span className="w-10 text-right text-gray-400">{percent}%</span>
		</div>
	)
}

function StarRating({ rating, onRate, readonly = false, size = 'sm' }) {
	const [hover, setHover] = useState(0)
	const sizeClass = size === 'lg' ? 'w-10 h-10' : size === 'md' ? 'w-6 h-6' : 'w-3 h-3'

	return (
		<div className={`flex ${size === 'lg' ? 'gap-2' : 'gap-0.5'}`}>
			{[1, 2, 3, 4, 5].map(star => {
				const filled = readonly ? star <= rating : star <= (hover || rating)
				const Icon = filled ? StarIcon : StarOutline
				return (
					<Icon
						key={star}
						className={`${sizeClass} ${
							filled ? 'text-yellow-400' : 'text-gray-300 dark:text-gray-600'
						} ${!readonly ? 'cursor-pointer transition-transform hover:scale-110' : ''}`}
						onMouseEnter={() => !readonly && setHover(star)}
						onMouseLeave={() => !readonly && setHover(0)}
						onClick={() => !readonly && onRate(star)}
					/>
				)
			})}
		</div>
	)
}

function formatDate(dateStr) {
	if (!dateStr) return null
	const date = new Date(dateStr)
	return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
}

function ReviewCard({ review, isGoogle, isDarkMode }) {
	const date = isGoogle ? review.time : formatDate(review.createdAt)

	return (
		<div className={`text-xs p-2 rounded ${isDarkMode ? 'bg-gray-800/50' : 'bg-white'}`}>
			<div className="flex items-center gap-2 mb-1">
				<StarRating rating={review.rating} readonly />
				<span className="text-[10px] text-gray-400">
					{review.authorName && `${review.authorName} • `}{date}
				</span>
				{isGoogle ? (
					<img src={googleIcon} alt="Google" className="w-3 h-3" />
				) : (
					<img src={loadfetcherLogo} alt="LoadFetcher" className="w-3 h-3" />
				)}
			</div>
			{review.text && (
				<p className={`text-[11px] ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{review.text}</p>
			)}
		</div>
	)
}

function MyReviewCard({ review, onEdit, onDelete, deleting, isDarkMode }) {
	const date = formatDate(review.createdAt)
	return (
		<div className={`p-3 rounded-lg ${isDarkMode ? 'bg-blue-900/20 border border-blue-800/50' : 'bg-blue-50 border border-blue-200'}`}>
			<div className="flex items-start justify-between gap-2">
				<div className="flex-1">
					<div className="flex items-center gap-2 mb-1">
						<span className={`text-[10px] font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>Your review</span>
						<img src={loadfetcherLogo} alt="LoadFetcher" className="w-3 h-3" />
					</div>
					<div className="flex items-center gap-2 mb-1">
						<StarRating rating={review.rating} readonly />
						<span className="text-[10px] text-gray-400">
							{review.isAnonymous ? 'Anonymous' : review.authorName}{date && ` • ${date}`}
						</span>
					</div>
					{review.text && (
						<p className={`text-[11px] ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{review.text}</p>
					)}
				</div>
				<div className="flex gap-0.5">
					<button
						onClick={onEdit}
						className={`p-1 text-gray-400 hover:text-blue-500 rounded transition-colors ${isDarkMode ? 'hover:bg-blue-900/50' : 'hover:bg-blue-100'}`}
						title="Edit review"
					>
						<PencilIcon className="w-3.5 h-3.5" />
					</button>
					<button
						onClick={onDelete}
						disabled={deleting}
						className={`p-1 text-gray-400 hover:text-red-500 rounded transition-colors disabled:opacity-50 ${isDarkMode ? 'hover:bg-red-900/50' : 'hover:bg-red-100'}`}
						title="Delete review"
					>
						<TrashIcon className="w-3.5 h-3.5" />
					</button>
				</div>
			</div>
		</div>
	)
}

function ReviewFormModal({ isOpen, onClose, onSubmit, isDarkMode, initialReview, error }) {
	const [rating, setRating] = useState(0)
	const [text, setText] = useState('')
	const [isAnonymous, setIsAnonymous] = useState(false)
	const [submitting, setSubmitting] = useState(false)
	const user = getCurrentUser()
	const isEditing = !!initialReview

	useEffect(() => {
		if (isOpen) {
			if (initialReview) {
				setRating(initialReview.rating || 0)
				setText(initialReview.text || '')
				setIsAnonymous(initialReview.isAnonymous || false)
			} else {
				setRating(0)
				setText('')
				setIsAnonymous(false)
			}
		}
	}, [isOpen, initialReview])

	const handleSubmit = async () => {
		if (rating === 0) return
		setSubmitting(true)
		const result = await onSubmit(rating, text, isAnonymous)
		setSubmitting(false)
		if (result) {
			onClose()
		}
	}

	if (!isOpen) return null

	return (
		<div className="fixed inset-0 z-[60] flex items-center justify-center">
			<div className="absolute inset-0 bg-black/60" onClick={onClose} />
			<div
				className={`relative w-full max-w-lg mx-4 rounded-xl shadow-2xl ${
					isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'
				}`}
			>
				<div className="p-6 space-y-6">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium">
								{isAnonymous ? 'A' : (user?.name?.[0] || 'U').toUpperCase()}
							</div>
							<div>
								<p className="font-medium">{isAnonymous ? 'Anonymous' : (user?.name || 'User')}</p>
								<p className="text-xs text-gray-500">
									{isAnonymous ? 'Posting anonymously' : 'Posting publicly on LoadFetcher'}
								</p>
							</div>
						</div>
						<label className="flex items-center gap-2 cursor-pointer">
							<span className="text-xs text-gray-500">Anonymous</span>
							<div
								className={`relative w-10 h-5 rounded-full transition-colors ${
									isAnonymous ? 'bg-blue-500' : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
								}`}
								onClick={() => setIsAnonymous(!isAnonymous)}
							>
								<div
									className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
										isAnonymous ? 'translate-x-5' : 'translate-x-0.5'
									}`}
								/>
							</div>
						</label>
					</div>

					<div className="flex justify-center py-2">
						<StarRating rating={rating} onRate={setRating} size="lg" />
					</div>

					<textarea
						value={text}
						onChange={e => setText(e.target.value)}
						placeholder="Share details of your experience at this facility"
						maxLength={500}
						rows={5}
						className={`w-full px-4 py-3 text-sm border rounded-lg bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${
							isDarkMode ? 'border-gray-600' : 'border-gray-300'
						}`}
					/>
					{error && <p className="text-sm text-red-500">{error}</p>}
				</div>

				<div className={`flex justify-end gap-3 px-6 py-4 border-t ${
					isDarkMode ? 'border-gray-700' : 'border-gray-200'
				}`}>
					<button
						onClick={onClose}
						className={`px-6 py-2 text-sm font-medium rounded-lg border ${
							isDarkMode
								? 'border-gray-600 hover:bg-gray-700'
								: 'border-gray-300 hover:bg-gray-100'
						}`}
					>
						Cancel
					</button>
					<button
						onClick={handleSubmit}
						disabled={rating === 0 || submitting}
						className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
					>
						{submitting ? (isEditing ? 'Updating...' : 'Posting...') : (isEditing ? 'Update' : 'Post')}
					</button>
				</div>
			</div>
		</div>
	)
}

function DeleteConfirmModal({ isOpen, onClose, onConfirm, deleting, isDarkMode, error }) {
	if (!isOpen) return null

	return (
		<div className="fixed inset-0 z-[60] flex items-center justify-center">
			<div className="absolute inset-0 bg-black/60" onClick={onClose} />
			<div
				className={`relative w-full max-w-sm mx-4 rounded-xl shadow-2xl p-6 ${
					isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'
				}`}
			>
				<h3 className="text-lg font-semibold mb-2">Delete review?</h3>
				<p className="text-sm text-gray-500 mb-6">
					This action cannot be undone. Your review will be permanently removed.
				</p>
				{error && <p className="text-sm text-red-500 mb-4">{error}</p>}
				<div className="flex justify-end gap-3">
					<button
						onClick={onClose}
						className={`px-4 py-2 text-sm font-medium rounded-lg border ${
							isDarkMode
								? 'border-gray-600 hover:bg-gray-700'
								: 'border-gray-300 hover:bg-gray-100'
						}`}
					>
						Cancel
					</button>
					<button
						onClick={onConfirm}
						disabled={deleting}
						className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
					>
						{deleting ? 'Deleting...' : 'Delete'}
					</button>
				</div>
			</div>
		</div>
	)
}

export default function FacilityStatsModal({ facilityCode, isOpen, onClose }) {
	const { isDarkMode } = useDarkMode()
	const [data, setData] = useState(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [showReviewForm, setShowReviewForm] = useState(false)
	const [editingReview, setEditingReview] = useState(null)
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
	const [deleting, setDeleting] = useState(false)
	const [deleteError, setDeleteError] = useState(null)
	const [submitError, setSubmitError] = useState(null)
	const [activeTab, setActiveTab] = useState('overview')
	const [allReviews, setAllReviews] = useState([])
	const [loadingAllReviews, setLoadingAllReviews] = useState(false)
	const [reviewsPage, setReviewsPage] = useState(1)
	const [hasMoreReviews, setHasMoreReviews] = useState(false)
	const [loadingMoreReviews, setLoadingMoreReviews] = useState(false)
	const [nearbyData, setNearbyData] = useState(null)
	const [loadingNearby, setLoadingNearby] = useState(false)
	const [nearbyView, setNearbyView] = useState('map')
	const [loadActivityData, setLoadActivityData] = useState(null)
	const [loadingLoadActivity, setLoadingLoadActivity] = useState(false)
	const [loadActivityPeriod, setLoadActivityPeriod] = useState('7d')
	const [loadActivityError, setLoadActivityError] = useState(null)
	const [isFullscreen, setIsFullscreen] = useState(false)
	const [statsPeriod, setStatsPeriod] = useState('7d')
	const nearbyMapRef = useRef(null)
	const nearbyMapContainerRef = useRef(null)

	const fetchData = useCallback((period = '7d') => {
		setLoading(true)
		setError(null)
		getFacilityStats(facilityCode, period)
			.then(result => {
				if (result) {
					setData(result)
				} else {
					setError('Failed to load facility data')
				}
			})
			.catch(err => {
				setError(err.response?.data?.error || err.message || 'Failed to load facility data')
			})
			.finally(() => setLoading(false))
	}, [facilityCode])

	const handleStatsPeriodChange = (period) => {
		if (period === statsPeriod) return
		setStatsPeriod(period)
		fetchData(period)
	}

	useEffect(() => {
		if (isOpen && facilityCode) {
			setStatsPeriod('7d')
			fetchData('7d')
			setActiveTab('overview')
			setAllReviews([])
			setReviewsPage(1)
			setHasMoreReviews(false)
			setNearbyData(null)
			setIsFullscreen(false)
			setLoadActivityData(null)
			setLoadActivityPeriod('7d')
			setLoadActivityError(null)
		}
	}, [isOpen, facilityCode, fetchData])

	const handleTabChange = async (tab) => {
		setActiveTab(tab)
		if (tab === 'reviews' && allReviews.length === 0) {
			setLoadingAllReviews(true)
			setReviewsPage(1)
			try {
				const result = await getAllFacilityReviews(facilityCode, 1, 20)
				if (result?.reviews) {
					setAllReviews(result.reviews)
					setHasMoreReviews(result.pagination?.hasMore || false)
				}
			} catch (err) {
				console.error('Failed to fetch reviews:', err)
			} finally {
				setLoadingAllReviews(false)
			}
		}
		if (tab === 'nearby' && !nearbyData) {
			setLoadingNearby(true)
			try {
				const result = await getFacilityNearby(facilityCode)
				setNearbyData(result)
			} catch (err) {
				console.error('Failed to fetch nearby stops:', err)
			} finally {
				setLoadingNearby(false)
			}
		}
		if (tab === 'load-activity' && !loadActivityData) {
			fetchLoadActivity('7d')
		}
	}

	const fetchLoadActivity = async (period) => {
		if (loadActivityData && loadActivityPeriod === period) return
		setLoadingLoadActivity(true)
		setLoadActivityPeriod(period)
		setLoadActivityError(null)
		try {
			const result = await getFacilityLoadActivity(facilityCode, period)
			setLoadActivityData(result)
		} catch (err) {
			console.error('Failed to fetch load activity:', err)
			setLoadActivityError(err.response?.data?.error || 'Failed to load activity data')
		} finally {
			setLoadingLoadActivity(false)
		}
	}

	const handleLoadMoreReviews = async () => {
		const nextPage = reviewsPage + 1
		setLoadingMoreReviews(true)
		try {
			const result = await getAllFacilityReviews(facilityCode, nextPage, 20)
			if (result?.reviews) {
				setAllReviews(prev => [...prev, ...result.reviews])
				setHasMoreReviews(result.pagination?.hasMore || false)
				setReviewsPage(nextPage)
			}
		} catch (err) {
			console.error('Failed to load more reviews:', err)
		} finally {
			setLoadingMoreReviews(false)
		}
	}

	useEffect(() => {
		if (!isOpen) return
		const handleKeyDown = e => {
			if (e.key === 'Escape') {
				if (isFullscreen) {
					setIsFullscreen(false)
				} else if (!showReviewForm && !showDeleteConfirm) {
					onClose()
				}
			}
		}
		document.addEventListener('keydown', handleKeyDown)
		return () => document.removeEventListener('keydown', handleKeyDown)
	}, [isOpen, onClose, showReviewForm, showDeleteConfirm, isFullscreen])

	const showReviewsTab = data?.rating?.totalCount > 5
	const tabs = [
		{ id: 'overview', label: 'Overview' },
		{ id: 'load-activity', label: 'Activity' },
		...(showReviewsTab ? [{ id: 'reviews', label: 'Reviews' }] : []),
		{ id: 'nearby', label: 'Nearby' },
	]

	useEffect(() => {
		const shouldShowMap = nearbyView === 'map' || isFullscreen
		if (activeTab !== 'nearby' || !shouldShowMap || !nearbyData || !nearbyMapContainerRef.current) return

		if (nearbyMapRef.current) {
			nearbyMapRef.current.remove()
			nearbyMapRef.current = null
		}

		const timer = setTimeout(() => {
			if (!nearbyMapContainerRef.current) return

			const map = L.map(nearbyMapContainerRef.current, {
				center: [nearbyData.facilityLat, nearbyData.facilityLon],
				zoom: 11,
				zoomControl: true,
			})

			const tileUrl = isDarkMode
				? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
				: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'

			L.tileLayer(tileUrl, {
				attribution: '© OpenStreetMap contributors',
				maxZoom: 19,
			}).addTo(map)

			const facilityIcon = L.divIcon({
				className: 'facility-marker',
				html: `<div style="background:#3b82f6;color:white;width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:11px;box-shadow:0 2px 6px rgba(0,0,0,0.4);">${escapeHtml(facilityCode.slice(0, 4))}</div>`,
				iconSize: [36, 36],
				iconAnchor: [18, 18],
			})

			L.marker([nearbyData.facilityLat, nearbyData.facilityLon], { icon: facilityIcon })
				.addTo(map)
				.bindPopup(`<b>${escapeHtml(facilityCode)}</b><br>Amazon Facility`)

			nearbyData.stops?.forEach(stop => {
				const icon = L.divIcon({
					className: 'stop-marker',
					html: `<div style="background:${stop.type === 'fuel_station' ? '#f59e0b' : '#10b981'};color:white;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:14px;border:2px solid white;box-shadow:0 2px 4px rgba(0,0,0,0.3);">${stop.type === 'fuel_station' ? '⛽' : '🅿️'}</div>`,
					iconSize: [28, 28],
					iconAnchor: [14, 14],
				})

				const directionsUrl = `https://www.google.com/maps/dir/?api=1&origin=${nearbyData.facilityLat},${nearbyData.facilityLon}&destination=${stop.lat},${stop.lon}&travelmode=driving`

				L.marker([stop.lat, stop.lon], { icon: icon })
					.addTo(map)
					.bindPopup(`<b>${escapeHtml(stop.name)}</b><br>${stop.distance.toFixed(1)} mi away<br><a href="${directionsUrl}" target="_blank" rel="noopener noreferrer" style="color:#3b82f6;">Get directions</a>`)
			})

			if (nearbyData.stops?.length > 0) {
				const bounds = L.latLngBounds([
					[nearbyData.facilityLat, nearbyData.facilityLon],
					...nearbyData.stops.map(s => [s.lat, s.lon])
				])
				map.fitBounds(bounds, { padding: [40, 40] })
			}

			nearbyMapRef.current = map
		}, 100)

		return () => {
			clearTimeout(timer)
			if (nearbyMapRef.current) {
				nearbyMapRef.current.remove()
				nearbyMapRef.current = null
			}
		}
	}, [activeTab, nearbyView, nearbyData, isDarkMode, facilityCode, isFullscreen])

	useEffect(() => {
		if (nearbyMapRef.current) {
			setTimeout(() => {
				nearbyMapRef.current?.invalidateSize()
			}, 100)
		}
	}, [isFullscreen])

	const handleSubmitReview = async (rating, text, isAnonymous) => {
		setSubmitError(null)
		try {
			const result = await submitFacilityReview(facilityCode, rating, text, isAnonymous, !!editingReview)
			if (result?.success) {
				setEditingReview(null)
				fetchData()
				return true
			}
			setSubmitError('Failed to submit review')
		} catch (err) {
			setSubmitError(err.response?.data?.error || 'Failed to submit review')
		}
		return false
	}

	const handleEditReview = () => {
		setEditingReview(data?.reviews?.myReview)
		setShowReviewForm(true)
	}

	const handleDeleteReview = async () => {
		setDeleting(true)
		setDeleteError(null)
		try {
			const result = await deleteFacilityReview(facilityCode)
			if (result?.success) {
				setShowDeleteConfirm(false)
				fetchData()
			} else {
				setDeleteError('Failed to delete review')
			}
		} catch (err) {
			setDeleteError(err.response?.data?.error || 'Failed to delete review')
		} finally {
			setDeleting(false)
		}
	}

	const handleCloseReviewForm = () => {
		setShowReviewForm(false)
		setEditingReview(null)
		setSubmitError(null)
	}

	if (!isOpen) return null

	const facility = data?.facilityInfo
	const stats = data?.stats
	const reviews = data?.reviews || []
	const myReview = data?.myReview

	return (
		<div className={`fixed inset-0 z-50 flex items-end justify-center ${isFullscreen ? 'pb-0' : 'pb-[20px]'}`}>
			<style>{`
				@keyframes slideUp {
					from { opacity: 0; transform: translateY(100%); }
					to { opacity: 1; transform: translateY(0); }
				}
				@keyframes glow {
					0%, 100% {
						box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25),
							0 -4px 16px rgba(74, 144, 226, 0.11),
							0 -2px 8px rgba(74, 144, 226, 0.074),
							0 0 0 1px rgba(74, 144, 226, 0.053),
							inset 0 1px 0 rgba(255, 255, 255, 0.032);
					}
					50% {
						box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25),
							0 -4px 16px rgba(74, 144, 226, 0.16),
							0 -2px 8px rgba(74, 144, 226, 0.11),
							0 0 0 1px rgba(74, 144, 226, 0.085),
							inset 0 1px 0 rgba(255, 255, 255, 0.042);
					}
				}
			`}</style>
			{!isFullscreen && <div className="absolute inset-0 bg-black/50" onClick={onClose} />}
			<div
				className={`relative w-full overflow-y-auto shadow-xl transition-all duration-200 ${
					isFullscreen
						? 'max-w-none mx-0 h-screen max-h-screen rounded-none'
						: 'max-w-[710px] mx-4 min-h-[calc(100vh-55px)] max-h-[calc(100vh-55px)] rounded-xl'
				} ${isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'}`}
				style={{
					animation: isDarkMode
						? 'slideUp 0.2s ease-out, glow 4s ease-in-out infinite 0.3s'
						: 'slideUp 0.2s ease-out',
				}}
			>
				{!isFullscreen && (
					<div className="sticky top-0 bg-inherit rounded-t-xl z-10">
						<div className="flex items-start justify-between py-4 px-6">
							<div>
								<div className="flex items-center gap-2">
									<h2 className="text-lg font-semibold">{facilityCode}</h2>
									{facility?.type && (
										<span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
											{facility.type}
										</span>
									)}
								</div>
								{facility && (
									<div className="flex items-center gap-1.5 mt-1 text-xs text-gray-500">
										<MapPinIcon className="w-3.5 h-3.5" />
										<span>{facility.address}, {facility.city}, {facility.state} {facility.zip}</span>
										{facility.googlePlaceId && (
											<a
												href={`https://www.google.com/maps/place/?q=place_id:${facility.googlePlaceId}`}
												target="_blank"
												rel="noopener noreferrer"
												className="text-blue-500 hover:text-blue-600"
											>
												<ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
											</a>
										)}
									</div>
								)}
							</div>
							<button
								onClick={onClose}
								className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
							>
								<XMarkIcon className="w-5 h-5" />
							</button>
						</div>
						<div className={`flex gap-1 px-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
							{tabs.map(tab => (
								<button
									key={tab.id}
									onClick={() => handleTabChange(tab.id)}
									className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
										activeTab === tab.id
											? 'border-blue-500 text-blue-600 dark:text-blue-400'
											: 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
									}`}
								>
									{tab.label}
								</button>
							))}
						</div>
					</div>
				)}

				<div className={isFullscreen ? 'p-0' : 'py-4 px-6 space-y-4'}>
					{activeTab === 'reviews' ? (
						loadingAllReviews ? (
							<FacilityLoader isDarkMode={isDarkMode} label="Loading reviews" />
						) : (
							<div className="space-y-4">
								<div className="flex items-center justify-between pb-4">
									{data?.rating?.average && (
										<div className="flex items-center gap-2">
											<span className="text-3xl font-semibold">{data.rating.average}</span>
											<div>
												<StarRating rating={Math.round(parseFloat(data.rating.average))} readonly />
												<p className="text-xs text-gray-500 mt-1">{data.rating.totalCount} reviews</p>
											</div>
										</div>
									)}
									{!myReview && (
										<button
											onClick={() => setShowReviewForm(true)}
											className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-300 dark:border-gray-600 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
										>
											<StarOutline className="w-4 h-4" />
											Write a review
										</button>
									)}
								</div>
								{myReview && (
									<MyReviewCard
										review={myReview}
										onEdit={handleEditReview}
										onDelete={() => setShowDeleteConfirm(true)}
										deleting={deleting}
										isDarkMode={isDarkMode}
									/>
								)}
								<div className="space-y-4">
									{allReviews.map((review, i) => (
										<ReviewCard
											key={i}
											review={review}
											isGoogle={review.source === 'google'}
											isDarkMode={isDarkMode}
										/>
									))}
								</div>
								{hasMoreReviews && (
									<div className="flex justify-center pt-4">
										<button
											onClick={handleLoadMoreReviews}
											disabled={loadingMoreReviews}
											className={`px-4 py-2 text-sm font-medium rounded-lg border ${
												isDarkMode
													? 'border-gray-600 hover:bg-gray-700'
													: 'border-gray-300 hover:bg-gray-100'
											} disabled:opacity-50`}
										>
											{loadingMoreReviews ? 'Loading...' : 'Load more'}
										</button>
									</div>
								)}
							</div>
						)
					) : activeTab === 'nearby' ? (
						loadingNearby ? (
							<FacilityLoader isDarkMode={isDarkMode} label="Loading nearby stops" />
						) : nearbyData?.stops?.length > 0 ? (
							<div className={isFullscreen ? '' : 'space-y-4'}>
								{!isFullscreen && (
									<div className="flex items-center justify-between">
										<p className="text-sm text-gray-500">
											{nearbyData.stops.length} stops within {nearbyData.radius} mi
										</p>
										<div className={`flex rounded-lg overflow-hidden border ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
											<button
												onClick={() => setNearbyView('map')}
												className={`px-3 py-1 text-xs font-medium ${
													nearbyView === 'map'
														? 'bg-blue-500 text-white'
														: isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
												}`}
											>
												Map
											</button>
											<button
												onClick={() => setNearbyView('list')}
												className={`px-3 py-1 text-xs font-medium ${
													nearbyView === 'list'
														? 'bg-blue-500 text-white'
														: isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
												}`}
											>
												List
											</button>
										</div>
									</div>
								)}

								{nearbyView === 'list' && !isFullscreen && (
									<div className="space-y-2">
										{nearbyData.stops.map((stop, i) => (
											<div
												key={i}
												className={`flex items-center gap-3 p-3 rounded-lg ${
													isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'
												}`}
											>
												<span className="text-xl leading-none flex-shrink-0">
													{stop.type === 'fuel_station' ? '⛽' : '🅿️'}
												</span>
												<div className="flex-1 min-w-0">
													<div className="flex items-center gap-2">
														<span className="font-medium text-sm truncate">{stop.name}</span>
														<span className="text-xs text-gray-500 whitespace-nowrap">
															{stop.distance.toFixed(1)} mi
														</span>
													</div>
													{stop.tags?.length > 0 && (
														<p className="text-xs text-gray-500 mt-0.5">
															{stop.tags.join(' · ')}
														</p>
													)}
												</div>
												<a
													href={`https://www.google.com/maps/dir/?api=1&origin=${nearbyData.facilityLat},${nearbyData.facilityLon}&destination=${stop.lat},${stop.lon}&travelmode=driving`}
													target="_blank"
													rel="noopener noreferrer"
													className="text-xs text-blue-500 hover:text-blue-600 whitespace-nowrap flex-shrink-0"
												>
													Directions
												</a>
											</div>
										))}
									</div>
								)}
								{(nearbyView === 'map' || isFullscreen) && (
									<div className="relative">
										<div className="absolute top-3 right-3 z-[1000] flex gap-2">
											<button
												onClick={() => setIsFullscreen(!isFullscreen)}
												className={`p-2 rounded-lg shadow-lg transition-colors ${
													isDarkMode ? 'bg-gray-800 text-white hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-100'
												}`}
												title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
											>
												{isFullscreen ? (
													<ArrowsPointingInIcon className="w-5 h-5" />
												) : (
													<ArrowsPointingOutIcon className="w-5 h-5" />
												)}
											</button>
											{isFullscreen && (
												<button
													onClick={onClose}
													className={`p-2 rounded-lg shadow-lg transition-colors ${
														isDarkMode ? 'bg-gray-800 text-white hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-100'
													}`}
													title="Close"
												>
													<XMarkIcon className="w-5 h-5" />
												</button>
											)}
										</div>
										<div
											ref={nearbyMapContainerRef}
											className={isFullscreen ? 'overflow-hidden' : 'rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden'}
											style={{
												height: isFullscreen ? '100vh' : 'calc(100vh - 250px)',
												minHeight: isFullscreen ? '100vh' : '420px',
												width: '100%'
											}}
										/>
									</div>
								)}
							</div>
						) : (
							<p className="text-center text-gray-500 py-8">
								No truck stops found nearby
							</p>
						)
					) : activeTab === 'load-activity' ? (
						<LoadActivityTab
							data={loadActivityData}
							period={loadActivityPeriod}
							onPeriodChange={fetchLoadActivity}
							loading={loadingLoadActivity}
							error={loadActivityError}
							isDarkMode={isDarkMode}
						/>
					) : loading ? (
						<FacilityLoader isDarkMode={isDarkMode} />
					) : error ? (
						<p className="text-center text-red-500 py-8">{error}</p>
					) : !data ? (
						<p className="text-center text-gray-500 py-8">No data available</p>
					) : (
						<>
							<div className="flex justify-end mb-2">
								<div className={`flex rounded-lg overflow-hidden border ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
									{['24h', '7d'].map(p => (
										<button
											key={p}
											onClick={() => handleStatsPeriodChange(p)}
											className={`px-2 py-0.5 text-xs font-medium ${
												statsPeriod === p
													? 'bg-blue-500 text-white'
													: isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
											}`}
										>
											{p}
										</button>
									))}
								</div>
							</div>

							{stats && (
								<div className={`rounded-lg p-3 ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
									<h3 className="font-medium text-xs mb-3">Early Check-in Distribution</h3>
									{stats.hasData ? (
									<>
										<div className="space-y-2">
											{Object.entries(DISTRIBUTION_LABELS).map(([key, label]) => (
												<DistributionBar
													key={key}
													label={label}
													percent={stats.distribution[key] || 0}
													isDarkMode={isDarkMode}
												/>
											))}
										</div>
										{stats.earlyNotAllowedPercent > 0 && (
											<p className="text-[10px] text-amber-600 dark:text-amber-400 mt-2">
												⚠️ Early check-in NOT allowed: {stats.earlyNotAllowedPercent}% of loads
											</p>
										)}
									</>
								) : (
									<p className="text-center text-gray-500 py-4 text-xs">
										No check-in data for this period
									</p>
								)}
								</div>
							)}

							<div className={`rounded-lg p-3 mt-2 ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
								<div className="flex items-center justify-between mb-3">
									<div className="flex items-center gap-2">
										<h3 className="font-medium text-xs">Reviews</h3>
										{data?.rating?.average && (
											<div className="flex items-center gap-1 text-xs">
												<StarIcon className="w-3 h-3 text-yellow-400" />
												<span>{data.rating.average}</span>
												<span className="text-gray-500">({data.rating.totalCount})</span>
											</div>
										)}
									</div>
									{!myReview && (
										<button
											onClick={() => setShowReviewForm(true)}
											className={`flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-medium rounded-md transition-colors ${
												isDarkMode
													? 'bg-blue-600 hover:bg-blue-700 text-white'
													: 'bg-blue-500 hover:bg-blue-600 text-white'
											}`}
										>
											<StarOutline className="w-3 h-3" />
											Write a review
										</button>
									)}
								</div>
								{myReview && (
									<MyReviewCard
										review={myReview}
										onEdit={handleEditReview}
										onDelete={() => setShowDeleteConfirm(true)}
										deleting={deleting}
										isDarkMode={isDarkMode}
									/>
								)}
								{reviews.length > 0 && (
									<div className={`space-y-2 ${myReview ? 'mt-2' : ''}`}>
										{reviews.map((review, i) => (
											<ReviewCard
												key={i}
												review={review}
												isGoogle={review.source === 'google'}
												isDarkMode={isDarkMode}
											/>
										))}
									</div>
								)}
								{!myReview && reviews.length === 0 && (
									<div className="flex flex-col items-center justify-center py-10 text-gray-400">
										<StarOutline className="w-10 h-10 mb-3" />
										<p className="text-sm font-medium">No reviews yet</p>
										<p className="text-xs mt-1">Be the first to share your experience</p>
									</div>
								)}
								{data?.rating?.totalCount > 5 && (
									<button
										className="w-full mt-3 py-1.5 text-[10px] text-blue-500 hover:text-blue-600 text-center"
										onClick={() => handleTabChange('reviews')}
									>
										View all {data.rating.totalCount} reviews
									</button>
								)}
							</div>

							{(!stats || !stats.hasData) && !facility && !myReview && reviews.length === 0 && (
								<p className="text-center text-gray-500 py-4">
									No analytics data available for this facility yet
								</p>
							)}
						</>
					)}
				</div>
			</div>

			<ReviewFormModal
				isOpen={showReviewForm}
				onClose={handleCloseReviewForm}
				onSubmit={handleSubmitReview}
				isDarkMode={isDarkMode}
				initialReview={editingReview}
				error={submitError}
			/>

			<DeleteConfirmModal
				isOpen={showDeleteConfirm}
				onClose={() => { setShowDeleteConfirm(false); setDeleteError(null) }}
				onConfirm={handleDeleteReview}
				deleting={deleting}
				isDarkMode={isDarkMode}
				error={deleteError}
			/>
		</div>
	)
}
